import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { DollarSign, Search, Share2, Printer, MoreVertical, Clock, CheckCircle, XCircle, Filter } from 'lucide-react';
import { useSales } from '../../../contexts/SalesContext';

// Estados de venta con sus colores y iconos
const SALE_STATUSES = {
  pending: { 
    label: 'PENDIENTES', 
    className: 'bg-amber-100 text-amber-700 border-amber-300 dark:bg-amber-900/50 dark:text-amber-400 dark:border-amber-800',
    icon: Clock
  },
  confirmed: { 
    label: 'CONFIRMADAS', 
    className: 'bg-blue-100 text-blue-700 border-blue-300 dark:bg-blue-900/50 dark:text-blue-400 dark:border-blue-800',
    icon: DollarSign
  },
  completed: { 
    label: 'COMPLETADAS', 
    className: 'bg-green-100 text-green-700 border-green-300 dark:bg-green-900/50 dark:text-green-400 dark:border-green-800',
    icon: CheckCircle
  },
  canceled: { 
    label: 'CANCELADAS', 
    className: 'bg-red-100 text-red-700 border-red-300 dark:bg-red-900/50 dark:text-red-400 dark:border-red-800',
    icon: XCircle
  }
};

const SaleCard = ({ sale, onClick }) => {
  // Mapear estados intermedios a los estados principales que queremos mostrar
  let displayStatus = sale.status;
  
  // Asegurarse de que 'canceled' se maneje correctamente
  if (sale.status === 'canceled') {
    displayStatus = 'canceled';
  } else if (sale.status === 'paid' || (sale.status !== 'pending' && sale.status !== 'completed')) {
    displayStatus = 'confirmed';
  }
  
  const status = SALE_STATUSES[displayStatus];
  const date = new Date(sale.created_at);
  const formattedDate = `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')}, ${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;

  return (
    <div 
      className="flex bg-white dark:bg-gray-800 rounded-lg shadow-sm overflow-hidden cursor-pointer hover:shadow-md transition-shadow" 
      onClick={onClick}
    >
      {/* Estado + Info Básica (1/3) */}
      <div className={`w-1/3 p-4 space-y-2 ${status.className}`}>
        <div className="text-xl font-extrabold text-gray-900 dark:text-white">
          {sale.display_id}
        </div>
        <div className="text-base text-gray-900 dark:text-white">
          {status.label}
        </div>
        <div className="text-sm text-gray-700 dark:text-gray-300 flex items-center">
          <Clock className="w-4 h-4 mr-1" />
          {formattedDate}
        </div>
      </div>
      
      {/* Detalles (2/3) */}
      <div className="w-2/3 p-4 space-y-2">
        <div className="text-lg font-medium text-gray-900 dark:text-white">
          {sale.client?.name || sale.customer_name || 'Sin cliente'}
        </div>
        <div className="text-sm text-gray-600 dark:text-gray-300">
          {sale.items.map((item, idx) => (
            <span key={idx}>
              {item.quantity} {item.unit} {item.title}
              {idx < sale.items.length - 1 ? ', ' : ''}
            </span>
          ))}
        </div>
        <div className="text-xl font-bold text-gray-900 dark:text-white">
          ${(Number(sale.total_amount || 0) + Number(sale.total_deposit_amount || 0)).toLocaleString()}
        </div>
      </div>
    </div>
  );
};

const SalesScreen = () => {
  const navigate = useNavigate();
  const { sales } = useSales();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState(() => {
    // Recuperar el último filtro usado o usar un valor por defecto
    const savedFilter = localStorage.getItem('salesStatusFilter');
    return savedFilter || 'confirmed'; // Por defecto mostrar confirmadas
  });
  const [showActions, setShowActions] = useState(false);

  // Guardar el filtro seleccionado en localStorage
  useEffect(() => {
    localStorage.setItem('salesStatusFilter', statusFilter);
  }, [statusFilter]);

  // Función auxiliar para obtener el estado de visualización
  const getDisplayStatus = (status) => {
    if (status === 'canceled') {
      return 'canceled';
    }
    if (status === 'paid' || (status !== 'pending' && status !== 'completed')) {
      return 'confirmed';
    }
    return status;
  };

  // Filtrar ventas según búsqueda y estado
  const filteredSales = sales.filter(sale => {
    const matchesSearch = (
      (sale.display_id || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (sale.client?.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      sale.items.some(item => 
        item.title?.toLowerCase().includes(searchTerm.toLowerCase())
      )
    );
    
    // Si no hay filtro seleccionado, mostrar todas las ventas
    if (!statusFilter) return matchesSearch;
    
    const displayStatus = getDisplayStatus(sale.status);
    const matchesStatus = displayStatus === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  // Función auxiliar para contar ventas por estado
  const countSalesByStatus = (status) => {
    return sales.filter(sale => getDisplayStatus(sale.status) === status).length;
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 pb-20">
      {/* Header */}
      <div className="sticky top-0 bg-white dark:bg-gray-800 border-b dark:border-gray-700 z-10">
        <div className="p-4 flex items-center justify-between">
          <div className="w-5"></div> {/* Spacer para equilibrar el botón de la derecha */}
          <h1 className="text-xl font-bold text-gray-900 dark:text-white">Ventas</h1>
          <div className="relative">
            <button 
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full text-gray-700 dark:text-gray-300"
              onClick={() => setShowActions(!showActions)}
            >
              <MoreVertical className="w-5 h-5" />
            </button>
            {showActions && (
              <>
                <div 
                  className="fixed inset-0 z-20"
                  onClick={() => setShowActions(false)}
                />
                <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border dark:border-gray-700 py-1 z-30">
                  <button className="w-full px-4 py-2 text-left flex items-center text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700">
                    <Share2 className="w-4 h-4 mr-2" />
                    Compartir listado
                  </button>
                  <button className="w-full px-4 py-2 text-left flex items-center text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700">
                    <Printer className="w-4 h-4 mr-2" />
                    Imprimir listado
                  </button>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Filtro de estados mejorado */}
        <div className="px-4 py-3 overflow-x-auto">
          <div className="flex gap-2 pb-1 overflow-x-auto hide-scrollbar">
            {Object.entries(SALE_STATUSES).map(([key, status]) => {
              const Icon = status.icon;
              
              return (
                <button
                  key={key}
                  onClick={() => setStatusFilter(statusFilter === key ? null : key)}
                  className={`flex items-center gap-1.5 px-3 py-2 rounded-full border whitespace-nowrap transition-all ${
                    statusFilter === key 
                      ? `${status.className} border-current font-medium` 
                      : 'bg-white text-gray-500 border-gray-200 dark:bg-gray-800 dark:text-gray-400 dark:border-gray-700'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span>{status.label}</span>
                </button>
              );
            })}
          </div>
        </div>
        
        {/* Búsqueda y Filtros */}
        <div className="p-4 space-y-2">
          <div className="relative">
            <Search className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500" />
            <input
              type="text"
              placeholder="Buscar por código, cliente o producto..."
              className="w-full pl-10 pr-4 py-2 border dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-600"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          {/* Contador de ventas */}
          <div className="px-1 text-sm text-gray-500 dark:text-gray-400">
            <span>{filteredSales.length} ventas</span>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-4">
        <div className="space-y-3">
          {filteredSales.length === 0 ? (
            <div className="text-center text-gray-500 dark:text-gray-400 py-8">
              No hay ventas que coincidan con tu búsqueda
            </div>
          ) : (
            filteredSales.map(sale => (
              <SaleCard 
                key={sale.id} 
                sale={sale}
                onClick={() => navigate(`/sales/${sale.id}`)}
              />
            ))
          )}
        </div>
      </div>

      {/* 
      <button
        onClick={() => navigate('/sales/new')}
        className="fixed right-4 bottom-20 w-14 h-14 bg-primary-500 text-white rounded-full shadow-lg flex items-center justify-center hover:bg-primary-600 transition-colors"
      >
        <DollarSign className="w-6 h-6" />
      </button>
      */}
    </div>
  );
};

export default SalesScreen; 